// Transient HUD messages: centre banners (ui:banner), stacked toasts
// (ui:toast), the damage / low-HP vignette and the interaction prompt.

import Phaser from 'phaser';
import { COLORS, css, mix } from '../../config/palette';
import { ActionHint, TYPE, UI, chamferPoints, glowTexture, txt, vignetteTexture } from '../kit';

// ───────────────────────────── Banners ─────────────────────────────

interface BannerReq {
  title: string;
  subtitle?: string;
  color: number;
}

/** Big centred title + subtitle. Requests queue so two banners never overlap. */
export class BannerQueue {
  private readonly root: Phaser.GameObjects.Container;
  private readonly lines: Phaser.GameObjects.Graphics;
  private readonly glow: Phaser.GameObjects.Image;
  private readonly title: Phaser.GameObjects.Text;
  private readonly sub: Phaser.GameObjects.Text;
  private readonly queue: BannerReq[] = [];
  private busy = false;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly y = 300,
  ) {
    this.root = scene.add.container(UI.W / 2, y).setDepth(50).setVisible(false);
    this.glow = scene.add.image(0, 10, glowTexture(scene)).setDisplaySize(1100, 220).setBlendMode(Phaser.BlendModes.ADD).setAlpha(0.2);
    this.lines = scene.add.graphics();
    this.title = txt(scene, 0, 0, '', { font: 'display', size: 76, weight: 700, color: 0xffffff, spacing: 10, originX: 0.5, originY: 0.5 });
    this.sub = txt(scene, 0, 62, '', { font: 'display', size: TYPE.h3, weight: 600, color: COLORS.uiText, spacing: 3, originX: 0.5, originY: 0.5 });
    this.root.add([this.glow, this.lines, this.title, this.sub]);
  }

  push(req: BannerReq): void {
    // Drop exact duplicates already waiting (e.g. the same synergy toggling twice).
    if (this.queue.some((q) => q.title === req.title && q.subtitle === req.subtitle)) return;
    if (this.queue.length >= 4) this.queue.shift();
    this.queue.push(req);
    if (!this.busy) this.next();
  }

  private next(): void {
    const req = this.queue.shift();
    if (!req) {
      this.busy = false;
      this.root.setVisible(false);
      return;
    }
    this.busy = true;
    const reduced = !!this.reduced?.();
    const col = req.color;
    this.title.setText(req.title.toUpperCase()).setFontSize(76);
    if (this.title.width > 1500) this.title.setFontSize(Math.floor((76 * 1500) / this.title.width));
    this.title.setShadow(0, 0, css(col, 0.85), reduced ? 10 : 24, false, true);
    this.title.setColor(css(mix(col, 0xffffff, 0.55)));
    this.sub.setText(req.subtitle ?? '').setColor(css(mix(col, 0xffffff, 0.2)));
    this.glow.setTint(col);
    const half = Math.max(this.title.width, this.sub.width) / 2 + 90;
    const g = this.lines;
    g.clear();
    g.fillStyle(col, 0.9);
    g.fillRect(-half, -54, half * 2, 2);
    g.fillRect(-half, req.subtitle ? 96 : 52, half * 2, 2);
    g.fillStyle(col, 1);
    g.fillPoints(chamferPoints(-half - 8, -59, 12, 12, { tl: 6, br: 6 }), true);
    g.fillPoints(chamferPoints(half - 4, (req.subtitle ? 96 : 52) - 5, 12, 12, { tl: 6, br: 6 }), true);
    this.root.setVisible(true).setAlpha(0).setY(this.y);
    g.setScale(0.2, 1);
    this.title.setX(-60);
    this.sub.setX(60);
    const tw = this.scene.tweens;
    tw.add({ targets: this.root, alpha: 1, duration: 200 });
    tw.add({ targets: g, scaleX: 1, duration: 380, ease: 'Cubic.easeOut' });
    tw.add({ targets: [this.title, this.sub], x: 0, duration: 420, ease: 'Cubic.easeOut' });
    tw.add({
      targets: this.root,
      alpha: 0,
      y: this.y - 24,
      delay: 2300,
      duration: 380,
      ease: 'Cubic.easeIn',
      onComplete: () => this.next(),
    });
  }

  /** Reduced-flashing accessor injected by the HUD. */
  reduced?: () => boolean;
}

// ───────────────────────────── Toasts ─────────────────────────────

interface Toast {
  root: Phaser.GameObjects.Container;
  h: number;
  dying: boolean;
}

/** Short notifications stacked bottom-centre (newest at the bottom). */
export class ToastStack {
  private readonly toasts: Toast[] = [];

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly baseY = 860,
  ) {}

  push(text: string, color: number = COLORS.uiText, durationMs = 2600): void {
    const s = this.scene;
    const root = s.add.container(UI.W / 2, this.baseY).setDepth(40);
    const t = txt(s, 0, 0, text, { font: 'body', size: TYPE.body, weight: 700, color: mix(color, 0xffffff, 0.35), originX: 0.5, originY: 0.5 });
    const w = Math.max(240, t.width + 64);
    const h = 46;
    const g = s.add.graphics();
    g.fillStyle(COLORS.uiBg, 0.88);
    g.fillPoints(chamferPoints(-w / 2, -h / 2, w, h, { tl: 10, br: 10 }), true);
    g.lineStyle(1.5, color, 0.75);
    g.strokePoints(chamferPoints(-w / 2, -h / 2, w, h, { tl: 10, br: 10 }), true, true);
    g.fillStyle(color, 1);
    g.fillRect(-w / 2 + 10, -h / 2 + 10, 3, h - 20);
    t.setX(6);
    root.add([g, t]);
    const toast: Toast = { root, h, dying: false };
    this.toasts.push(toast);
    while (this.toasts.filter((x) => !x.dying).length > 4) this.kill(this.toasts.find((x) => !x.dying)!);
    this.layout();
    root.setAlpha(0).setScale(0.92);
    s.tweens.add({ targets: root, alpha: 1, scale: 1, duration: 200, ease: 'Back.easeOut' });
    s.time.delayedCall(durationMs, () => this.kill(toast));
  }

  private kill(t: Toast): void {
    if (t.dying) return;
    t.dying = true;
    this.scene.tweens.add({
      targets: t.root,
      alpha: 0,
      duration: 260,
      onComplete: () => {
        t.root.destroy();
        const i = this.toasts.indexOf(t);
        if (i >= 0) this.toasts.splice(i, 1);
        this.layout();
      },
    });
  }

  private layout(): void {
    let y = this.baseY;
    for (let i = this.toasts.length - 1; i >= 0; i--) {
      const t = this.toasts[i];
      if (t.dying) continue;
      this.scene.tweens.add({ targets: t.root, y, duration: 180, ease: 'Cubic.easeOut' });
      y -= t.h + 8;
    }
  }
}

// ───────────────────────────── Vignette ─────────────────────────────

/** Red edge flash on damage and a slow pulse while HP is critical. */
export class DamageVignette {
  private readonly img: Phaser.GameObjects.Image;
  private hit = 0;
  private lowT = 0;
  private low = 0;

  constructor(scene: Phaser.Scene) {
    const key = vignetteTexture(scene, 'ui_vignette_red', COLORS.threat, 0.6, 0.9);
    this.img = scene.add.image(0, 0, key).setOrigin(0, 0).setDisplaySize(UI.W, UI.H).setDepth(-10).setAlpha(0);
  }

  /** amount: fraction of max HP lost (scales the flash). */
  flash(amount: number, reduced: boolean): void {
    const k = Phaser.Math.Clamp(0.35 + amount * 2.2, 0.35, 1);
    this.hit = Math.max(this.hit, reduced ? k * 0.4 : k * 0.85);
  }

  tick(dt: number, hpFrac: number, reduced: boolean): void {
    this.hit = Math.max(0, this.hit - dt * (reduced ? 1.2 : 2.4));
    const critical = hpFrac > 0 && hpFrac < 0.25;
    this.low += ((critical ? 1 : 0) - this.low) * Math.min(1, dt * 3);
    this.lowT += dt;
    const pulse = reduced ? 0.22 : 0.18 + 0.16 * (0.5 + 0.5 * Math.sin(this.lowT * 4.2));
    const a = Math.min(1, this.hit + this.low * pulse);
    this.img.setAlpha(a).setVisible(a > 0.005);
  }
}

// ───────────────────────────── Interact prompt ─────────────────────────────

/** "[E] OPEN SERVICE STATION   [X] SALVAGE +12" just above the HUD deck. */
export class InteractPrompt {
  private readonly root: Phaser.GameObjects.Container;
  private readonly bg: Phaser.GameObjects.Graphics;
  private readonly main: ActionHint;
  private readonly alt: ActionHint;
  private key = '';

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly y = 892,
  ) {
    this.root = scene.add.container(UI.W / 2, y).setDepth(45).setAlpha(0).setVisible(false);
    this.bg = scene.add.graphics();
    this.main = new ActionHint(scene, 'interact', '', { size: 30, fontSize: 22 });
    this.alt = new ActionHint(scene, 'salvage', '', { size: 30, fontSize: 22, color: COLORS.scrap });
    this.root.add([this.bg, this.main, this.alt]);
  }

  set(prompt: string | null, alt?: string | null, enabled = true): void {
    const key = prompt ? `${prompt}|${alt ?? ''}|${enabled}` : '';
    if (key === this.key) return;
    const wasShown = this.key !== '';
    this.key = key;
    if (!prompt) {
      this.scene.tweens.killTweensOf(this.root);
      this.scene.tweens.add({ targets: this.root, alpha: 0, duration: 140, onComplete: () => this.root.setVisible(false) });
      return;
    }
    this.main.setLabel(prompt).setLabelColor(enabled ? COLORS.uiText : COLORS.uiDim);
    this.alt.setVisible(!!alt);
    if (alt) this.alt.setLabel(alt);
    const gap = 34;
    const total = this.main.width + (alt ? gap + this.alt.width : 0);
    this.main.x = -total / 2;
    this.alt.x = -total / 2 + this.main.width + gap;
    const w = total + 56;
    const h = 50;
    this.bg.clear();
    this.bg.fillStyle(COLORS.uiBg, 0.85);
    this.bg.fillPoints(chamferPoints(-w / 2, -h / 2, w, h, { tl: 10, br: 10 }), true);
    this.bg.lineStyle(1.5, COLORS.uiBorder, 1);
    this.bg.strokePoints(chamferPoints(-w / 2, -h / 2, w, h, { tl: 10, br: 10 }), true, true);
    this.root.setVisible(true);
    this.scene.tweens.killTweensOf(this.root);
    if (!wasShown) {
      this.root.setY(this.y + 10);
      this.scene.tweens.add({ targets: this.root, alpha: 1, y: this.y, duration: 160, ease: 'Cubic.easeOut' });
    } else this.root.setAlpha(1).setY(this.y);
  }
}
